import { proxy } from 'valtio';

import {
  type DrawerPanel,
  type LeftSidebarPanel,
  type RightSidebarPanel,
} from '@canvas/model/sidebar-panel';

export const defaultSidebarPanel = { panel: 'none' as RightSidebarPanel };
export const defaultDrawerPanel = { panel: 'none' as DrawerPanel };
export const defaultLeftSidebarPanel = { panel: 'none' as LeftSidebarPanel };
export const currentRightSidebarPanel: { panel: RightSidebarPanel } =
  proxy(defaultSidebarPanel);

/**
 * A drawer panel is a panel that is rendered outside of the canvas safe zone, and behaves like a sidebar,
 * however the presence of the drawer causes the UI elements of canvas to be moved to the side.
 * A drawer can also be open at the same time as a sidebar.
 * @see @canvas/module-types/src/ui/drawer/drawer.ts
 */
export const currentDrawerPanel: { panel: DrawerPanel | 'none' } =
  proxy(defaultDrawerPanel);

export const currentLeftSidebarPanel: { panel: LeftSidebarPanel } = proxy(
  defaultLeftSidebarPanel
);

export type MoreShapesSectionId =
  | 'basic'
  | 'connectors'
  | 'flowchart'
  | 'advanced'
  | 'recent-elements'
  | 'atlassian-architecture-icons'
  | 'aws-architecture-icons'
  | 'azure-architecture-icons'
  | 'gcp-architecture-icons'
  | 'cloud-icon-buttons'
  | 'uml';

export const isMoreShapesSectionId = (
  sectionId: string
): sectionId is MoreShapesSectionId => {
  return sectionId in moreShapesSectionState.expandedSections;
};

/**
 * More Shapes Sidebar Section State Management
 * true = expanded (showing content), false = collapsed (hiding content)
 * By default, all sections are expanded
 */
export const moreShapesSectionState = proxy<{
  expandedSections: Record<MoreShapesSectionId, boolean>;
}>({
  expandedSections: {
    basic: true,
    connectors: true,
    flowchart: true,
    advanced: true,
    'recent-elements': true,
    'atlassian-architecture-icons': true,
    'aws-architecture-icons': true,
    'azure-architecture-icons': true,
    'gcp-architecture-icons': true,
    'cloud-icon-buttons': true,
    uml: true,
  },
});

/**
 * Cloud Shapes Sidebar Section State Management
 * true = expanded (showing content), false = collapsed (hiding content),
 * undefined = collapsed (hiding content).
 * By default, all sections are expanded in Atlassian, and first three sections are expanded in AWS, Azure, and GCP
 */
export const cloudShapesSectionState = proxy<{
  expandedSections: Record<string, boolean>;
}>({
  expandedSections: {
    // Default all sections to be expanded in Atlassian
    'shapesSidebar-atlassian-teamwork': true,
    'shapesSidebar-atlassian-software': true,
    'shapesSidebar-atlassian-service': true,
    'shapesSidebar-atlassian-strategy': true,
    'shapesSidebar-atlassian-platform': true,
    'shapesSidebar-atlassian-other-products': true,
    'elementToolbar-atlassian-teamwork': true,
    'elementToolbar-atlassian-software': true,
    'elementToolbar-atlassian-service': true,
    'elementToolbar-atlassian-strategy': true,
    'elementToolbar-atlassian-platform': true,
    'elementToolbar-atlassian-other-products': true,
    // Default first three sections to be expanded in AWS
    'shapesSidebar-aws-analytics': true,
    'shapesSidebar-aws-application-integration': true,
    'shapesSidebar-aws-artificial-intelligence': true,
    'elementToolbar-aws-analytics': true,
    'elementToolbar-aws-application-integration': true,
    'elementToolbar-aws-artificial-intelligence': true,
    // Default first three sections to be expanded in Azure
    'shapesSidebar-azure-ai-machine-learning': true,
    'shapesSidebar-azure-analytics': true,
    'shapesSidebar-azure-app-services': true,
    'elementToolbar-azure-ai-machine-learning': true,
    'elementToolbar-azure-analytics': true,
    'elementToolbar-azure-app-services': true,
    // Default first three sections to be expanded in GCP
    'shapesSidebar-gcp-ai-and-machine-learning': true,
    'shapesSidebar-gcp-api-management': true,
    'shapesSidebar-gcp-ci-cd': true,
    'elementToolbar-gcp-ai-and-machine-learning': true,
    'elementToolbar-gcp-api-management': true,
    'elementToolbar-gcp-ci-cd': true,
  },
});

export const isDrawerOpen = () => {
  return currentDrawerPanel.panel !== 'none';
};

export const updateRightSidebarPanel = (panel: RightSidebarPanel) => {
  currentRightSidebarPanel.panel = panel;
};

export const updateLeftSidebarPanel = (panel: LeftSidebarPanel) => {
  currentLeftSidebarPanel.panel = panel;
};

export const updateDrawerPanel = (panel: DrawerPanel) => {
  currentDrawerPanel.panel = panel;
};

export const resetRightSidebarPanel = () => {
  currentRightSidebarPanel.panel = defaultSidebarPanel.panel;
};

export const resetLeftSidebarPanel = () => {
  currentLeftSidebarPanel.panel = defaultLeftSidebarPanel.panel;
};

export const resetDrawerPanel = () => {
  currentDrawerPanel.panel = defaultDrawerPanel.panel;
};
